import React, { useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";

/*
 * Tabbed persona landing page (thariq.io style): a pill toggle switches
 * between the Professional side (bio, experience, projects) and the
 * Personal side (posts, community).
 */

const LINKS = [
  { label: "GitHub", href: "https://github.com/pyoos" },
  { label: "LinkedIn", href: "https://www.linkedin.com/in/pyoo1" },
  { label: "Email", href: "mailto:inisgood@gmail.com" },
];

const EXPERIENCE = [
  {
    org: "Plasmole",
    period: "Jan 2026 – Present",
    blurb:
      "AI-native molecular biology platform — plasmid design, electronic lab notebook, and lab-workflow automation in one connected workspace.",
    href: "https://plasmole.com",
    logo: "/logos/plasmole.png",
  },
  {
    org: "YS Studio",
    period: "Coming soon",
    blurb: "An independent design & software studio. More soon.",
    logo: "ysstudio",
  },
  {
    org: "Koju",
    period: "Coming soon",
    blurb: "Something new in the works. More soon.",
    logo: "letter:K",
  },
  {
    org: "Faltas Lab",
    period: "2023 – Present",
    blurb:
      "Cancer biology research at Weill Cornell Medicine. Built the lab's grant-management system and purchasing Slackbot.",
    logo: "/logos/weill-cornell.png",
    cite: {
      label: "Co-author · Bose et al., 2025 (bioRxiv) ↗",
      href: "https://www.biorxiv.org/content/10.1101/2025.10.21.682588v1",
    },
  },
  {
    org: "Wave Dating App",
    period: "2020 – Present",
    blurb: "Built a video-first dating app — 1.3K+ downloads, 4.9/5 App Store rating.",
    href: "http://wavedatingapp.com/index.html",
    logo: "/logos/wave.png",
  },
  {
    org: "Aquavit Pharmaceuticals",
    period: "2021",
    blurb: "Intern.",
    href: "https://www.aquavitpharma.com",
    logo: "/logos/aquavit.png",
  },
  {
    org: "Temple University, College of Engineering",
    period: "2019 – 2020",
    blurb: "Undergraduate TA.",
    href: "https://www.temple.edu",
    logo: "/logos/temple.png",
  },
];

const PROJECTS = [
  {
    title: "UNet Single-Cell Segmentation",
    blurb: "Deep learning pipeline for single-cell segmentation in microscopy images.",
    href: "https://github.com/pyoos/UNet_SingleCellSegmentation",
    tag: "AI/ML",
  },
  {
    title: "DDS70",
    blurb: "YOLOv8 basketball tracking & shot analytics, trained on a 10K-image dataset.",
    href: "https://github.com/pyoos/DoesDarrenShootLessThan70-",
    tag: "Computer Vision",
  },
  {
    title: "Faltas Lab Slackbot",
    blurb: "Slack bot automating lab purchase requests end-to-end.",
    href: "https://github.com/pyoos/Faltas-Slackbot",
    tag: "Automation",
  },
  {
    title: "Faltas Lab GMS",
    blurb: "Grant management system tracking spending across lab grants.",
    href: "https://github.com/pyoos/Faltas_GMS",
    tag: "Tooling",
  },
  {
    title: "Wave (app + site)",
    blurb: "React Native video-first dating platform and its marketing site.",
    href: "http://wavedatingapp.com/index.html",
    tag: "Mobile",
  },
];

const POSTS = [
  {
    title: "First Post",
    date: "Coming soon",
    read: "1 min",
    to: "/posts/first-post",
  },
];

const COMMUNITY = [
  {
    title: "Associate Director of Development",
    org: "KCUL Foundation",
    period: "2019 – Present",
    blurb:
      "Fundraising & PR for the annual Kimchi Festival at Love Park — thousands of attendees, $50k+ in grants, coverage on WHYY, Fox, YTN.",
    logo: "/logos/kcul.png",
  },
  {
    title: "Volunteer Basketball Coach",
    org: "Apex for Youth",
    period: "2024 – Present",
    blurb:
      "Coaching Asian immigrant youth (ages 8–13) in fundamentals, teamwork, and confidence.",
    href: "https://www.apexfornyc.org",
    logo: "/logos/apex.png",
  },
];

const serif = { fontFamily: "Georgia, serif" };

function SunIcon() {
  return (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden="true">
      <circle cx="12" cy="12" r="4.5" />
      <line x1="12" y1="2" x2="12" y2="4.5" />
      <line x1="12" y1="19.5" x2="12" y2="22" />
      <line x1="2" y1="12" x2="4.5" y2="12" />
      <line x1="19.5" y1="12" x2="22" y2="12" />
      <line x1="4.8" y1="4.8" x2="6.6" y2="6.6" />
      <line x1="17.4" y1="17.4" x2="19.2" y2="19.2" />
      <line x1="4.8" y1="19.2" x2="6.6" y2="17.4" />
      <line x1="17.4" y1="6.6" x2="19.2" y2="4.8" />
    </svg>
  );
}

function MoonIcon() {
  return (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M21 12.79A9 9 0 1 1 11.21 3a7 7 0 0 0 9.79 9.79z" />
    </svg>
  );
}

/** PY monogram: staggered serif letters in a rounded frame. */
function LogoPY({ dark }) {
  const ink = dark ? "#f4f4f5" : "#18181b";
  return (
    <svg
      width="40"
      height="40"
      viewBox="0 0 48 48"
      role="img"
      aria-label="PY logo"
      className="shrink-0"
    >
      <rect
        x="2.5"
        y="2.5"
        width="43"
        height="43"
        rx="10"
        fill="none"
        stroke={ink}
        strokeWidth="2.4"
      />
      <text
        x="15.5"
        y="31.5"
        textAnchor="middle"
        fontFamily="Georgia, serif"
        fontSize="24"
        fontWeight="bold"
        fill={ink}
      >
        P
      </text>
      <text
        x="31.5"
        y="37"
        textAnchor="middle"
        fontFamily="Georgia, serif"
        fontSize="24"
        fontWeight="bold"
        fill={ink}
      >
        Y
      </text>
      {/* hairline separating the staggered letters */}
      <line x1="24.6" y1="10" x2="22.2" y2="38" stroke={ink} strokeWidth="1" opacity="0.35" />
    </svg>
  );
}

/** YS Studio mark: gradient rounded badge with a serif YS monogram. */
function LogoYSStudio() {
  return (
    <svg width="36" height="36" viewBox="0 0 40 40" role="img" aria-label="YS Studio" className="shrink-0">
      <defs>
        <linearGradient id="ys-grad" x1="0" y1="0" x2="1" y2="1">
          <stop offset="0" stopColor="#6366f1" />
          <stop offset="1" stopColor="#a855f7" />
        </linearGradient>
      </defs>
      <rect x="1" y="1" width="38" height="38" rx="11" fill="url(#ys-grad)" />
      <text
        x="20"
        y="26"
        textAnchor="middle"
        fontFamily="Georgia, serif"
        fontSize="15"
        fontWeight="bold"
        fill="#fff"
        letterSpacing="-0.5"
      >
        YS
      </text>
    </svg>
  );
}

/** Fallback letter badge for orgs without a brand logo. */
function LogoLetter({ label, dark }) {
  const ink = dark ? "#f4f4f5" : "#18181b";
  const bg = dark ? "#27272a" : "#e4e4e7";
  return (
    <svg width="36" height="36" viewBox="0 0 40 40" role="img" aria-label={label} className="shrink-0">
      <rect x="1" y="1" width="38" height="38" rx="11" fill={bg} />
      <text
        x="20"
        y="26"
        textAnchor="middle"
        fontFamily="Georgia, serif"
        fontSize="18"
        fontWeight="bold"
        fill={ink}
      >
        {label}
      </text>
    </svg>
  );
}

/** Picks the right logo for an experience entry. */
function ExpLogo({ logo, org, dark }) {
  if (logo === "ysstudio") return <LogoYSStudio />;
  if (logo && logo.startsWith("letter:")) return <LogoLetter label={logo.slice(7)} dark={dark} />;
  if (logo)
    return (
      <img
        src={logo}
        alt={`${org} logo`}
        className={`w-9 h-9 rounded-lg object-contain shrink-0 ${
          dark ? "ring-1 ring-zinc-700" : "bg-white p-0.5 ring-1 ring-zinc-200/80"
        }`}
      />
    );
  return <LogoLetter label={org.charAt(0)} dark={dark} />;
}

const tabContent = {
  hidden: { opacity: 0, y: 10 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.25, ease: "easeOut" } },
  exit: { opacity: 0, y: -10, transition: { duration: 0.15, ease: "easeIn" } },
};

export default function Home({ darkMode, setDarkMode }) {
  const [tab, setTab] = useState(() =>
    new URLSearchParams(window.location.search).get("tab") === "personal"
      ? "personal"
      : "professional"
  );

  const border = darkMode ? "border-zinc-800" : "border-zinc-300/70";
  const divide = darkMode ? "divide-zinc-800" : "divide-zinc-300/70";
  const subtle = darkMode ? "text-zinc-400" : "text-zinc-600";
  const faint = "text-zinc-500";
  const hoverCard = darkMode ? "hover:bg-zinc-900" : "hover:bg-zinc-100/80";
  const pillBg = darkMode ? "bg-zinc-900" : "bg-zinc-200/70";
  const pillActive = darkMode ? "bg-zinc-100 text-zinc-900" : "bg-white text-zinc-900 shadow-sm";

  const SectionHead = ({ children, to, linkLabel }) => (
    <div className="flex items-baseline justify-between mb-4">
      <h3 className="text-lg font-semibold" style={serif}>
        {children}
      </h3>
      {to && (
        <Link to={to} className={`text-xs ${faint} hover:opacity-70`}>
          {linkLabel} →
        </Link>
      )}
    </div>
  );

  return (
    <div
      className={`split-home min-h-screen ${
        darkMode ? "bg-zinc-950 text-zinc-100" : "bg-[#faf8f3] text-zinc-900"
      }`}
    >
      {/* Top bar */}
      <header
        className={`max-w-2xl mx-auto flex items-center justify-between px-6 py-6`}
      >
        <LogoPY dark={darkMode} />
        <div className="flex items-center gap-4 text-sm">
          <a href="/?intro=1" className={`${subtle} hover:opacity-70 transition-opacity`}>
            Visit the lab
          </a>
        </div>
      </header>

      {/* Persona tabs + theme toggle */}
      <div className="max-w-2xl mx-auto px-6 mt-4 mb-10 flex items-center gap-3">
        <div className={`relative flex rounded-full p-1 flex-1 ${pillBg}`}>
          {[
            { id: "professional", label: "Professional" },
            { id: "personal", label: "Personal" },
          ].map((t) => (
            <button
              key={t.id}
              onClick={() => setTab(t.id)}
              className={`relative flex-1 py-2 rounded-full text-sm font-medium transition-colors z-10 ${
                tab === t.id ? "" : `${subtle} hover:opacity-80`
              }`}
            >
              {tab === t.id && (
                <motion.span
                  layoutId="tab-pill"
                  className={`absolute inset-0 rounded-full ${pillActive}`}
                  transition={{ type: "spring", stiffness: 400, damping: 32 }}
                />
              )}
              <span className={`relative ${tab === t.id ? (darkMode ? "text-zinc-900" : "text-zinc-900") : ""}`}>
                {t.label}
              </span>
            </button>
          ))}
        </div>

        <button
          onClick={() => setDarkMode((d) => !d)}
          aria-label={darkMode ? "Switch to light mode" : "Switch to dark mode"}
          title={darkMode ? "Switch to light mode" : "Switch to dark mode"}
          className={`h-10 w-10 shrink-0 rounded-full flex items-center justify-center transition-colors ${pillBg} ${
            darkMode
              ? "text-amber-300 hover:bg-zinc-800"
              : "text-zinc-600 hover:bg-zinc-300/80"
          }`}
        >
          <AnimatePresence mode="wait" initial={false}>
            <motion.span
              key={darkMode ? "moon" : "sun"}
              initial={{ rotate: -90, opacity: 0, scale: 0.6 }}
              animate={{ rotate: 0, opacity: 1, scale: 1 }}
              exit={{ rotate: 90, opacity: 0, scale: 0.6 }}
              transition={{ duration: 0.18 }}
              className="flex"
            >
              {darkMode ? <MoonIcon /> : <SunIcon />}
            </motion.span>
          </AnimatePresence>
        </button>
      </div>

      {/* Tab content */}
      <main className="max-w-2xl mx-auto px-6 pb-16">
        <AnimatePresence mode="wait">
          {tab === "professional" ? (
            <motion.section
              key="professional"
              variants={tabContent}
              initial="hidden"
              animate="visible"
              exit="exit"
            >
              <h2 className="text-3xl md:text-4xl font-bold leading-tight mb-5" style={serif}>
                Paul Yoo.
              </h2>
              <p className={`${subtle} leading-relaxed mb-4`}>
                I work at the intersection of biomedical research and software — currently at
                the Faltas Lab at Weill Cornell Medicine.
              </p>
              <div className="flex gap-4 mb-12 text-sm">
                {LINKS.map((l) => (
                  <a
                    key={l.label}
                    href={l.href}
                    target={l.href.startsWith("http") ? "_blank" : undefined}
                    rel="noopener noreferrer"
                    className="underline underline-offset-4 decoration-1 hover:opacity-70 transition-opacity"
                  >
                    {l.label}
                  </a>
                ))}
              </div>

              <div className="mb-12">
                <SectionHead to="/work" linkLabel="full timeline">
                  Experience
                </SectionHead>
                <ul className={`divide-y ${divide}`}>
                  {EXPERIENCE.map((e) => (
                    <li key={e.org} className="py-4 flex gap-3">
                      <ExpLogo logo={e.logo} org={e.org} dark={darkMode} />
                      <div className="flex-1 min-w-0">
                        <div className="flex items-baseline justify-between gap-3">
                          <p className="font-medium">
                            {e.href ? (
                              <a
                                href={e.href}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="hover:opacity-70 transition-opacity"
                              >
                                {e.org} <span className="opacity-40">↗</span>
                              </a>
                            ) : (
                              e.org
                            )}
                          </p>
                          <span className={`text-xs whitespace-nowrap ${faint}`}>{e.period}</span>
                        </div>
                        <p className={`text-sm mt-1 ${subtle}`}>{e.blurb}</p>
                        {e.cite && (
                          <a
                            href={e.cite.href}
                            target="_blank"
                            rel="noopener noreferrer"
                            className={`inline-block text-sm mt-1 underline underline-offset-2 decoration-1 hover:opacity-70 ${subtle}`}
                          >
                            {e.cite.label}
                          </a>
                        )}
                      </div>
                    </li>
                  ))}
                </ul>
              </div>

              <div>
                <SectionHead to="/projects" linkLabel="all projects">
                  Stuff I've Made
                </SectionHead>
                <div className="grid gap-3">
                  {PROJECTS.map((p) => (
                    <a
                      key={p.title}
                      href={p.href}
                      target="_blank"
                      rel="noopener noreferrer"
                      className={`block rounded-lg border ${border} p-4 transition-colors ${hoverCard}`}
                    >
                      <div className="flex items-baseline justify-between gap-3">
                        <p className="font-medium">{p.title}</p>
                        <span
                          className={`text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full border ${border} ${faint}`}
                        >
                          {p.tag}
                        </span>
                      </div>
                      <p className={`text-sm mt-1 ${subtle}`}>{p.blurb}</p>
                    </a>
                  ))}
                </div>
              </div>
            </motion.section>
          ) : (
            <motion.section
              key="personal"
              variants={tabContent}
              initial="hidden"
              animate="visible"
              exit="exit"
            >
              <h2 className="text-3xl md:text-4xl font-bold leading-tight mb-5" style={serif}>
                Trying to build things that matter.
              </h2>
              <p className={`${subtle} leading-relaxed mb-12`}>
                Outside the lab I coach basketball, help run a cultural festival, and write
                about whatever I'm learning. This side of the page is for the thoughts that
                don't fit on a resume.
              </p>

              <div className="mb-12">
                <SectionHead to="/posts" linkLabel="all posts">
                  Posts
                </SectionHead>
                <ul className={`divide-y ${divide}`}>
                  {POSTS.map((p) => (
                    <li key={p.title}>
                      <Link
                        to={p.to}
                        className={`flex items-baseline justify-between gap-3 py-4 transition-colors ${hoverCard} -mx-2 px-2 rounded`}
                      >
                        <span className="font-medium">{p.title}</span>
                        <span className={`text-xs whitespace-nowrap ${faint}`}>
                          {p.date} · {p.read}
                        </span>
                      </Link>
                    </li>
                  ))}
                </ul>
                <p className={`text-sm mt-4 italic ${faint}`}>
                  More writing on research, engineering, and everything in between — soon.
                </p>
              </div>

              <div>
                <SectionHead>Community</SectionHead>
                <ul className={`divide-y ${divide}`}>
                  {COMMUNITY.map((c) => (
                    <li key={c.title} className="py-4 flex gap-3">
                      <ExpLogo logo={c.logo} org={c.org} dark={darkMode} />
                      <div className="flex-1 min-w-0">
                        <div className="flex items-baseline justify-between gap-3">
                          <p className="font-medium">
                            {c.title}{" "}
                            <span className={faint}>
                              ·{" "}
                              {c.href ? (
                                <a
                                  href={c.href}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="hover:opacity-70 transition-opacity"
                                >
                                  {c.org} <span className="opacity-40">↗</span>
                                </a>
                              ) : (
                                c.org
                              )}
                            </span>
                          </p>
                          <span className={`text-xs whitespace-nowrap ${faint}`}>{c.period}</span>
                        </div>
                        <p className={`text-sm mt-1 ${subtle}`}>{c.blurb}</p>
                      </div>
                    </li>
                  ))}
                </ul>
              </div>
            </motion.section>
          )}
        </AnimatePresence>
      </main>

      <footer className={`max-w-2xl mx-auto px-6 py-8 border-t ${border} text-xs ${faint}`}>
        © {new Date().getFullYear()} Paul Yoo · built with a 3D lab you can{" "}
        <a href="/?intro=1" className="underline underline-offset-2 hover:opacity-70">
          walk around in
        </a>
      </footer>
    </div>
  );
}
